import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IconButton } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { reelPlacement, wrapOffset } from '../../../lib/wins-reel';
import styles from './WinsReel.module.css';

const IDLE_AFTER_MS = 6000;
const IDLE_SPEED = 0.0024;
const EASE = 0.14;
const DRAG_PX_PER_CARD = 300;
const WHEEL_PX_PER_CARD = 320;

export interface ReelItem {
  key: string;
  render: (focused: boolean) => ReactNode;
  /** Left out of the "X of Y" count, so a period's recap card does not count as one of its wins. */
  uncounted?: boolean;
}

/**
 * One win in focus, the rest curving away on a ring. The ring turns in a requestAnimationFrame loop
 * that writes each slot's transform straight to the DOM, so React re-renders only when the focused
 * card changes. Drag, the wheel, the arrow keys, the buttons and a click on a card all move `target`;
 * after six seconds with no touch the ring drifts on its own, unless the reader prefers reduced motion
 * or `paused` is set (the share dialog is open).
 */
export function WinsReel({ items, paused, onFocusChange }: { items: ReelItem[]; paused: boolean; onFocusChange?: (index: number) => void }) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const slots = useRef<(HTMLDivElement | null)[]>([]);
  const state = useRef({ pos: 0, target: 0, dragging: false, hovering: false, lastTouch: Date.now(), moved: false, downX: 0, downPos: 0 });
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const [focus, setFocus] = useState(0);
  const focusRef = useRef(0);
  const n = items.length;
  const countRef = useRef(n);
  countRef.current = n;

  const reduceMotion = useRef(false);
  useEffect(() => {
    reduceMotion.current = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const s = state.current;
    s.pos = n > 1 && !reduceMotion.current ? -1.6 : 0;
    s.target = 0;
    s.lastTouch = Date.now();
  }, [n]);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const s = state.current;
      const count = countRef.current;
      if (!s.dragging) {
        const idle = !reduceMotion.current && !s.hovering && !pausedRef.current && Date.now() - s.lastTouch > IDLE_AFTER_MS && count > 1;
        if (idle) {
          s.target += IDLE_SPEED;
          s.pos = s.target;
        } else if (reduceMotion.current) {
          s.pos = s.target;
        } else {
          s.pos += (s.target - s.pos) * EASE;
          if (Math.abs(s.target - s.pos) < 0.0005) s.pos = s.target;
        }
      }
      for (let i = 0; i < count; i += 1) {
        const el = slots.current[i];
        if (!el) continue;
        const p = reelPlacement(wrapOffset(i - s.pos, count));
        el.style.transform = p.transform;
        el.style.filter = p.filter;
        el.style.opacity = p.opacity;
        el.style.zIndex = p.zIndex;
      }
      const focused = count ? ((Math.round(s.pos) % count) + count) % count : 0;
      if (focused !== focusRef.current) {
        focusRef.current = focused;
        setFocus(focused);
        onFocusChange?.(focused);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onFocusChange]);

  const touch = () => {
    state.current.lastTouch = Date.now();
  };
  const step = useCallback((k: number) => {
    const s = state.current;
    s.lastTouch = Date.now();
    s.target = Math.round(s.target) + k;
  }, []);
  const goTo = (i: number) => {
    const s = state.current;
    s.lastTouch = Date.now();
    s.target = Math.round(s.target + wrapOffset(i - s.target, countRef.current));
  };

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (e: WheelEvent) => {
      const dv = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!dv) return;
      e.preventDefault();
      const s = state.current;
      s.lastTouch = Date.now();
      s.target += dv / WHEEL_PX_PER_CARD;
      clearTimeout(settle);
      settle = setTimeout(() => {
        s.target = Math.round(s.target);
      }, 140);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      clearTimeout(settle);
    };
  }, []);

  return (
    <div className={styles.wrap}>
      <div
        ref={rootRef}
        className={styles.ring}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={t('wins.reel.label')}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            step(1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            step(-1);
          }
        }}
        onPointerDown={(e) => {
          if (e.button !== 0 || (e.target as HTMLElement).closest('button, a')) return;
          const s = state.current;
          s.dragging = true;
          s.moved = false;
          s.downX = e.clientX;
          s.downPos = s.pos;
          touch();
          rootRef.current?.classList.add(styles.dragging);
          try {
            rootRef.current?.setPointerCapture(e.pointerId);
          } catch {
            /* capture is best-effort */
          }
        }}
        onPointerMove={(e) => {
          const s = state.current;
          if (!s.dragging) return;
          const dx = e.clientX - s.downX;
          if (Math.abs(dx) > 4) s.moved = true;
          s.pos = s.downPos - dx / DRAG_PX_PER_CARD;
          s.target = s.pos;
        }}
        onPointerUp={(e) => {
          const s = state.current;
          if (!s.dragging) return;
          s.dragging = false;
          rootRef.current?.classList.remove(styles.dragging);
          touch();
          if (!s.moved) {
            const slot = (e.target as HTMLElement).closest<HTMLElement>('[data-reel-index]');
            const i = slot ? Number(slot.dataset.reelIndex) : -1;
            if (i > -1) goTo(i);
            else s.target = Math.round(s.pos);
          } else {
            s.target = Math.round(s.pos);
          }
        }}
        onPointerCancel={() => {
          const s = state.current;
          s.dragging = false;
          s.target = Math.round(s.pos);
          rootRef.current?.classList.remove(styles.dragging);
        }}
        onPointerEnter={() => {
          const s = state.current;
          s.hovering = true;
          s.target = Math.round(s.pos);
        }}
        onPointerLeave={() => {
          state.current.hovering = false;
          touch();
        }}
      >
        {items.map((item, i) => (
          <div
            key={item.key}
            ref={(el) => {
              slots.current[i] = el;
            }}
            className={`${styles.slot}${i === focus ? ` ${styles.focus}` : ''}`}
            data-reel-index={i}
            aria-hidden={i === focus ? undefined : true}
          >
            {item.render(i === focus)}
          </div>
        ))}
      </div>
      {n > 1 && (
        <>
          <div className={styles.controls}>
            <IconButton variant="outlined" aria-label={t('wins.reel.prev')} onClick={() => step(-1)}>
              <ChevronLeft size={20} aria-hidden className="dir-flip" />
            </IconButton>
            <span className={`sym-type-small-regular ${styles.count}`} aria-live="polite">
              {items[focus]?.uncounted
                ? null
                : t('wins.reel.count', {
                    index: items.slice(0, focus + 1).filter((item) => !item.uncounted).length,
                    total: items.filter((item) => !item.uncounted).length,
                  })}
            </span>
            <IconButton variant="outlined" aria-label={t('wins.reel.next')} onClick={() => step(1)}>
              <ChevronRight size={20} aria-hidden className="dir-flip" />
            </IconButton>
          </div>
          <p className={`sym-type-tiny-regular ${styles.hint}`}>{t('wins.reel.hint')}</p>
        </>
      )}
    </div>
  );
}
